import type { DailyPayRow } from '@/app/features/employee/emp-payroll';
import * as XLSX from 'xlsx';

/**
 * Builds the full-cycle sheet in the browser.
 *
 * The server's DAILY template only ever carries a single date, so the cycle
 * sheet is made here instead. It uses the server's own columns (Employee ID,
 * Name, Email, User ID, then one column per date) and its `Yes` / `No` values,
 * so the upload endpoint reads it exactly like its own template.
 */
export const downloadCycleTemplate = (
  rows: DailyPayRow[],
  dates: string[],
  fileName: string,
) => {
  const sheetRows = rows.map((row) => {
    const answers = new Map(
      row.entries.map((entry) => [entry.date, entry.is_payable]),
    );
    return {
      'Employee ID': row.employee_id,
      Name: row.name,
      Email: row.email || '',
      'User ID': row.user_id,
      // Pre-filled with what is stored, so a single cell can be corrected
      // and the same file uploaded straight back.
      ...Object.fromEntries(
        dates.map((date) => [date, answers.get(date) ? 'Yes' : 'No']),
      ),
    };
  });

  const header = ['Employee ID', 'Name', 'Email', 'User ID', ...dates];
  const sheet = XLSX.utils.json_to_sheet(sheetRows, { header });
  sheet['!cols'] = [
    { wch: 14 },
    { wch: 24 },
    { wch: 30 },
    { wch: 38 },
    ...dates.map(() => ({ wch: 11 })),
  ];

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Payroll Calendar');
  XLSX.writeFile(book, fileName);
};
